import React, { useState, useEffect } from 'react';
import { Formik, Form, Field, ErrorMessage } from 'formik';
import { TextField } from 'formik-material-ui';
import Link from 'next/link';
import DocumentHead from 'components/DocumentHead';
import Grid from '@material-ui/core/Grid';
import Button from '@material-ui/core/Button';
import InputAdornment from '@material-ui/core/InputAdornment';
import PhoneInput from 'react-phone-number-input';
import * as yup from 'yup';
import clsx from 'clsx';

import { postRequest } from 'utils/api';
import { getLastSignInRole } from 'utils/localStorage';
import useScrollToTop from 'hooks/useScrollToTop';
import Email from 'public/icons/form/email.svg';
import Switch from 'components/Switch';
import styles from 'styles/components/auth/AuthForm.module.scss';

const marginBottom = { marginBottom: 30 };
const fullWidth = { width: '100%' };

const defaultRequired = 'is required.';

const schema = yup.object({
  phone: yup.number().required(defaultRequired),
  email: yup
    .string()
    .email('is not a valid email.')
    .required(defaultRequired),
});

const initialValues = {
  phone: undefined,
  email: '',
};

const ForgotPassword = () => {
  const [role, setRole] = useState('influencer');
  const [resetLinkSent, setResetLinkSent] = useState(false);
  useScrollToTop();

  const getErrors = errors => (errors ? true : false);

  const handleSubmit = (values, actions) => {
    const login = role === 'influencer' ? values.phone : values.email;

    setResetLinkSent(true);

    postRequest({
      endpoint: 'password',
      data: { user: { login } },
      version: null,
      successMessage: `We just sent you a reset password link. Please check your ${
        role === 'influencer' ? 'phone' : 'email'
      }.`,
      ...actions,
    })
      .then(() => {
        actions.resetForm();
      })
      .catch(() => {});
  };

  useEffect(() => {
    const lastSignInRole = getLastSignInRole();

    if (lastSignInRole) {
      setRole(lastSignInRole);
    } else {
      setRole('influencer');
    }
  }, []);

  return (
    <Grid container className={styles.container} direction="column" alignItems="center">
      <DocumentHead>
        <title>Forgot Password | GigSocial</title>
      </DocumentHead>

      <h1>Forgot Password?</h1>

      <Grid item xs={11} sm={6} md={4} lg={3} style={fullWidth}>
        <div style={marginBottom}>
          <Switch
            firstOption={{ label: 'Influencer', value: 'influencer' }}
            secondOption={{ label: 'Advertiser', value: 'advertiser' }}
            setValue={value => setRole(value)}
            value={role}
          />
        </div>

        <Formik validationSchema={schema} initialValues={initialValues} noValidate>
          {({ handleBlur, setFieldValue, touched, values, actions, errors }) => (
            <Form className={styles.form}>
              {role === 'influencer' ? (
                <>
                  <PhoneInput
                    name="phone"
                    placeholder="Phone Number"
                    onChange={phone => setFieldValue('phone', phone)}
                    onBlur={handleBlur}
                    defaultCountry="US"
                    defaultChecked
                    className={clsx(styles.phoneInput, {
                      [styles.phoneInputError]: touched.phone && !values.phone,
                    })}
                  />

                  <ErrorMessage component="span" name="phone" className={styles.error} />
                </>
              ) : (
                <>
                  <Field
                    component={TextField}
                    name="email"
                    type="email"
                    placeholder="Email"
                    variant="outlined"
                    color="primary"
                    InputProps={{
                      startAdornment: (
                        <InputAdornment position="start">
                          <Email width={14} />
                        </InputAdornment>
                      ),
                    }}
                  />
                </>
              )}

              <Button
                variant="contained"
                color="primary"
                disabled={
                  resetLinkSent ||
                  (role === 'influencer'
                    ? !values.phone || getErrors(errors.phone)
                    : !values.email || getErrors(errors.email))
                }
                type="submit"
                fullWidth
                onClick={() => handleSubmit(values, actions)}
              >
                Send
              </Button>
            </Form>
          )}
        </Formik>

        <div className={styles.goToLogin}>
          <Link href="/login"> Go to Login</Link>
        </div>
      </Grid>
    </Grid>
  );
};

export default ForgotPassword;
